import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { SignInPage } from '@/features/auth/pages/SignInPage';
import { CoursesPage } from '@/features/courses/pages/CoursesPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { HomePage } from '@/features/home/pages/HomePage';
import { LoadingScreen } from '@/components/ui/LoadingScreen';

const CoursePage    = lazy(() => import('@/features/courses/pages/CoursePage').then(m => ({ default: m.CoursePage })));
const LessonPage    = lazy(() => import('@/features/courses/pages/LessonPage').then(m => ({ default: m.LessonPage })));
const JoinPage      = lazy(() => import('@/features/game/pages/JoinPage').then(m => ({ default: m.JoinPage })));
const PlayPage      = lazy(() => import('@/features/game/pages/PlayPage').then(m => ({ default: m.PlayPage })));
const HostPage      = lazy(() => import('@/features/game/pages/HostPage').then(m => ({ default: m.HostPage })));
const ReviewPage    = lazy(() => import('@/features/game/pages/ReviewPage').then(m => ({ default: m.ReviewPage })));
const QRPage        = lazy(() => import('@/features/game/pages/QRPage').then(m => ({ default: m.QRPage })));
const DisplayPage   = lazy(() => import('@/features/game/pages/DisplayPage').then(m => ({ default: m.DisplayPage })));
const TestPage      = lazy(() => import('@/features/test/pages/TestPage').then(m => ({ default: m.TestPage })));
const GameTestPage  = lazy(() => import('@/features/test/pages/GameTestPage').then(m => ({ default: m.GameTestPage })));
const AccountPage   = lazy(() => import('@/features/account/pages/AccountPage').then(m => ({ default: m.AccountPage })));
const SurveyRedirectPage = lazy(() => import('@/features/survey/SurveyRedirectPage').then(m => ({ default: m.SurveyRedirectPage })));
const ReportLayout    = lazy(() => import('@/features/report/pages/ReportLayout').then(m => ({ default: m.ReportLayout })));
const ResearchPage    = lazy(() => import('@/features/report/pages/ResearchPage').then(m => ({ default: m.ResearchPage })));
const WorkshopsPage   = lazy(() => import('@/features/report/pages/WorkshopsPage').then(m => ({ default: m.WorkshopsPage })));
const PrototypesPage  = lazy(() => import('@/features/report/pages/PrototypesPage').then(m => ({ default: m.PrototypesPage })));

export const router = createBrowserRouter([
  {
    element: (
      <AppShell>
        <ScrollRestoration />
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </AppShell>
    ),
    children: [
      { path: '/courses', element: <CoursesPage /> },
      { path: '/courses/:slug', element: <CoursePage /> },
      { path: '/courses/:slug/lessons/:lessonId', element: <LessonPage /> },
      { path: '/sign-in', element: <SignInPage /> },
      { path: '/join', element: <JoinPage /> },
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/uitest',
        element: (
          <ProtectedRoute>
            <TestPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/gametest',
        element: (
          <ProtectedRoute>
            <GameTestPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/account',
        element: (
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/sessions/:sessionId',
        element: (
          <ProtectedRoute>
            <HostPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/sessions/:sessionId/review',
        element: (
          <ProtectedRoute>
            <ReviewPage />
          </ProtectedRoute>
        ),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
  {
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: '/report',
        element: <ReportLayout />,
        children: [
          { index: true, element: <Navigate to="/report/workshops" replace /> },
          { path: 'research',   element: <ResearchPage /> },
          { path: 'workshops',  element: <WorkshopsPage /> },
          { path: 'prototypes', element: <PrototypesPage /> },
        ],
      },
    ],
  },
  {
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/pre', element: <SurveyRedirectPage phase="pre" /> },
      { path: '/post', element: <SurveyRedirectPage phase="post" /> },
      { path: '/sa-pre', element: <SurveyRedirectPage phase="sa-pre" /> },
      { path: '/sa-post', element: <SurveyRedirectPage phase="sa-post" /> },
      { path: '/play/:sessionId', element: <PlayPage /> },
      { path: '/qr/:code', element: <QRPage /> },
      { path: '/display/:sessionId', element: <DisplayPage /> },
    ],
  },
]);
